/**
 * Benchmark — the screen. The airport's standing on every service metric
 * against the local, regional and global peer sets, ranked only among peers
 * with an entered value, and the form to enter those values with their
 * source and year.
 */
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useSession } from "../../app/SessionContext";
import {
  answerBenchmark,
  BENCHMARK_MODEL_PROMPT,
  buildBenchmark,
  materialForBenchmark,
  METRICS,
  type BenchmarkDesk,
  type BenchmarkEntered,
  type Peer,
  type PeerSet,
} from "../../domain/benchmark";
import { Button, Card, ErrorState, Field, Input, Select, Skeleton } from "../../design/components";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { useFinance } from "./FinanceForm";
import { AskCard, fmt, Refs, Tiles, x as s } from "./shared";
import { useEntered } from "./store";
import { useGreenSummary } from "../green/useGreen";
import { useScreenContext } from "../../assistant/screen";
import { PeersCard } from "../entryForms/PeersCard";

export function BenchmarkPage() {
  const session = useSession();
  const source = session.source;
  const siteId = session.site.id;
  const kpis = useQuery({ queryKey: ["kpis", siteId], staleTime: 60_000, queryFn: () => source.listKpis(siteId) });
  const finance = useFinance();
  const green = useGreenSummary();
  const entered = useEntered<BenchmarkEntered>("executive.benchmark", {});
  const [set, setSet] = useState<PeerSet>("regional");
  const desk = useMemo<BenchmarkDesk | null>(() => {
    if (!kpis.data) return null;
    return buildBenchmark({
      now: new Date(),
      siteCode: session.site.code,
      currency: finance.value.currency,
      kpis: kpis.data,
      finance: finance.value,
      green,
      entered: entered.value,
    });
  }, [kpis.data, finance.value, green, entered.value, session.site.code]);
  useScreenContext(
    desk
      ? {
          route: "/benchmark",
          title: "Benchmark",
          summary: desk.boardLine,
          material: materialForBenchmark(desk),
          figures: desk.figures,
          own: (q) => answerBenchmark(q, desk),
          prompt: BENCHMARK_MODEL_PROMPT,
          offers: [
            { label: "Where are we weakest?", ask: "where are we weakest regionally?" },
            { label: "What is blank?", ask: "what is blank?" },
            { label: "The board line", ask: "give me the board line" },
          ],
        }
      : null,
  );
  return (
    <Page
      eyebrow={t("{site} · Executive", { site: session.site.code })}
      title="Benchmark"
      actions={
        <Button variant="primary" onClick={() => window.print()}>
          {t("Print")}
        </Button>
      }
    >
      <p className={s.lead}>
        {t(
          "How the airport compares with peers in the Kingdom, the Gulf, and worldwide.",
        )}
      </p>
      {kpis.isPending && <Skeleton rows={8} />}
      {kpis.isError && <ErrorState error={kpis.error} retry={() => void kpis.refetch()} />}
      {desk && (
        <div className={s.page}>
          <Tiles items={desk.tiles} currency={desk.currency} />
          {entered.savedTo && <p className={s.saved}>{entered.savedTo}</p>}
          <Card
            title="Standing by metric"
            sub="Our value, rank, leader, and gap"
            actions={
              <div className={s.row}>
                {(["local", "regional", "global"] as PeerSet[]).map((k) => (
                  <Button
                    key={k}
                    size="sm"
                    variant={set === k ? "primary" : "default"}
                    onClick={() => setSet(k)}
                    aria-pressed={set === k}
                  >
                    {t(k)}
                  </Button>
                ))}
              </div>
            }
          >
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>{t("metric")}</th>
                    <th>{t("own")}</th>
                    <th>{t("rank")}</th>
                    <th>{t("leader")}</th>
                    <th>{t("gap")}</th>
                    <th>{t("blank peers")}</th>
                  </tr>
                </thead>
                <tbody>
                  {desk.standings.map((st) => {
                    const b = st.bySet[set];
                    return (
                      <tr key={st.metric.key} data-testid={`metric-${st.metric.key}`}>
                        <td>
                          <b>{t(st.metric.label)}</b>
                          <div className={s.muted}>{t(st.metric.what)}</div>
                          <Refs list={st.ownSource} />
                        </td>
                        <td className={s.num}>
                          {st.own != null ? (
                            `${fmt(st.own, st.metric.unit === "SAR" ? desk.currency : "", desk.currency)} ${st.metric.unit}`
                          ) : (
                            <span className={s.blank}>{t("no value")}</span>
                          )}
                          {st.own != null && st.ownAssumed && <div className={s.tag}>{t("demo / entered")}</div>}
                        </td>
                        <td className={s.num}>
                          {b.rank != null ? (
                            t("{rank} of {of}", { rank: b.rank, of: b.of + 1 })
                          ) : (
                            <span className={s.blank}>{t("unranked")}</span>
                          )}
                        </td>
                        <td>
                          {b.leader ? (
                            `${b.leader.name} · ${b.leader.value} ${st.metric.unit}`
                          ) : (
                            <span className={s.blank}>—</span>
                          )}
                        </td>
                        <td
                          className={`${s.num} ${b.gapToLeader != null ? ((st.metric.direction === "up" ? b.gapToLeader >= 0 : b.gapToLeader <= 0) ? s.pos : s.neg) : ""}`}
                        >
                          {b.gapToLeader != null ? `${b.gapToLeader > 0 ? "+" : ""}${b.gapToLeader}` : "—"}
                        </td>
                        <td className={s.num}>{t("{blank} of {total}", { blank: b.blank, total: b.blank + b.of })}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
          {/* build 6B: the peer list and every entered value can be changed or removed */}
          <PeersCard
            entered={entered.value}
            list={desk.peers}
            siteCode={session.site.code}
            ready={!entered.loading}
            onSave={(e) => entered.save(e)}
          />
          <AskCard
            title="The line for the board"
            sub="Summary for the board, from the airport's own figures"
            board={desk.boardLine}
            material={materialForBenchmark(desk)}
            figures={desk.figures}
            own={(q) => answerBenchmark(q, desk)}
            prompt={BENCHMARK_MODEL_PROMPT}
            placeholder={t("Where are we weakest regionally? What leads globally? What is blank?")}
          />
          <details className={s.drawer}>
            <summary>
              {t("Add a peer's published figure, or one of ours")}
            </summary>
            <div className={s.drawerBody}>
              {!entered.loading && <PeerForm key={entered.at ?? "seed"} entered={entered.value} peers={desk.peers} siteCode={session.site.code} onSave={(e) => void entered.save(e)} />}
            </div>
          </details>
          {desk.gaps.length > 0 && (
            <Card title="What is blank" sub="so the reader knows what the ranks leave out">
              <ul className={s.plain}>
                {desk.gaps.slice(0, 12).map((g) => (
                  <li key={g}>{g}</li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}
    </Page>
  );
}

function PeerForm({
  entered,
  peers,
  siteCode,
  onSave,
}: {
  entered: BenchmarkEntered;
  peers: Peer[];
  siteCode: string;
  onSave: (e: BenchmarkEntered) => void;
}) {
  const [peer, setPeer] = useState<string>(peers[0]?.code ?? siteCode);
  const [metric, setMetric] = useState<string>(METRICS[0].key);
  const [value, setValue] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear() - 1));
  const [src, setSrc] = useState("");
  const count = Object.values(entered.peers ?? {}).reduce((t, m) => t + Object.keys(m).length, 0);
  return (
    <Card
      title="Peer and own values"
      sub={t(
        '{n} peer values entered; the airport\'s own ASQ score, ACA level and turnaround are entered as "this airport"',
        { n: count },
      )}
    >
      <form
        className={s.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (value === "") return;
          const v = { value: Number(value), year: year ? Number(year) : undefined, source: src || undefined };
          if (peer === siteCode) onSave({ ...entered, own: { ...entered.own, [metric]: v } });
          else onSave({ ...entered, peers: { ...entered.peers, [peer]: { ...entered.peers?.[peer], [metric]: v } } });
          setValue("");
          setSrc("");
        }}
      >
        <Field label="airport">
          <Select value={peer} onChange={(e) => setPeer(e.target.value)} aria-label={t("peer airport")}>
            <option value={siteCode}>{t("this airport ({code})", { code: siteCode })}</option>
            {peers.map((p) => (
              <option key={p.code} value={p.code}>
                {p.name} · {t(p.set)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="metric">
          <Select value={metric} onChange={(e) => setMetric(e.target.value)} aria-label={t("metric")}>
            {METRICS.map((m) => (
              <option key={m.key} value={m.key}>
                {t(m.label)} ({m.unit})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="value">
          <Input
            type="number"
            step="any"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            aria-label={t("peer value")}
          />
        </Field>
        <Field label="year">
          <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} aria-label={t("peer year")} />
        </Field>
        <Field label="source">
          <Input
            value={src}
            onChange={(e) => setSrc(e.target.value)}
            placeholder={t("ACI ASQ 2025; the airport's sustainability report")}
            aria-label={t("peer source")}
          />
        </Field>
        <Button type="submit" disabled={value === ""}>
          {t("Enter the value")}
        </Button>
      </form>
    </Card>
  );
}
